'use client';
import * as React from 'react';
import invariant from 'invariant';
import useEventCallback from '@mui/utils/useEventCallback';

import { DialogsContext } from './DialogsContext';
import { OpenDialog, DialogComponent, OpenDialogOptions } from './useDialogs';

interface DialogStackEntry<P, R> {
  payload: P;
  key: string;
  open: boolean;
  promise: Promise<R>;
  resolve: (result: R) => void;
  Component: DialogComponent<P, R>;
  onClose: (result: R) => Promise<void>;
}

export interface DialogProviderProps {
  unmountAfter?: number;
  children?: React.ReactNode;
}

/**
 * Provider for Dialog stacks. The subtree of this component can use the `useDialogs` hook to
 * access the dialogs API. The dialogs are rendered in the order they are requested.
 *
 * Demos:
 *
 * - [useDialogs](https://mui.com/toolpad/core/react-use-dialogs/)
 *
 * API:
 *
 * - [DialogsProvider API](https://mui.com/toolpad/core/api/dialogs-provider)
 */
function DialogsProvider(props: DialogProviderProps) {
  const { children, unmountAfter = 1000 } = props;
  const [stack, setStack] = React.useState<DialogStackEntry<any, any>[]>([]);
  const keyPrefix = React.useId();
  const nextId = React.useRef(0);
  const dialogMetadata = React.useRef(
    new WeakMap<Promise<any>, DialogStackEntry<any, any>>()
  );

  const requestDialog = useEventCallback<OpenDialog>(function open<P, R>(
    Component: DialogComponent<P, R>,
    payload: P,
    options: OpenDialogOptions<R> = {}
  ) {
    const { onClose = async () => {} } = options;
    let resolve: ((result: R) => void) | undefined;
    const promise = new Promise<R>((resolveImpl) => {
      resolve = resolveImpl;
    });
    invariant(resolve, 'resolve not set');

    const key = `${keyPrefix}-${nextId.current}`;
    nextId.current += 1;

    const newEntry: DialogStackEntry<P, R> = {
      key,
      promise,
      payload,
      onClose,
      resolve,
      Component,
      open: true,
    };

    // Store metadata for reliable access during close
    dialogMetadata.current.set(promise, newEntry);

    setStack((prevStack) => [...prevStack, newEntry]);
    return promise;
  });

  const closeDialogUi = useEventCallback(function closeDialogUi<R>(
    dialog: Promise<R>
  ) {
    setStack((prevStack) =>
      prevStack.map((entry) =>
        entry.promise === dialog ? { ...entry, open: false } : entry
      )
    );
    setTimeout(() => {
      // wait for closing animation
      setStack((prevStack) =>
        prevStack.filter((entry) => entry.promise !== dialog)
      );
      // WeakMap automatically cleans up when promise is garbage collected
    }, unmountAfter);
  });

  const closeDialog = useEventCallback(async function closeDialog<R>(
    dialog: Promise<R>,
    result: R
  ) {
    const entryToClose = dialogMetadata.current.get(dialog);
    invariant(entryToClose, 'dialog not found');

    try {
      await entryToClose.onClose(result);
    } finally {
      entryToClose.resolve(result);
      closeDialogUi(dialog);
    }
    return dialog;
  });

  const contextValue = React.useMemo(
    () => ({ close: closeDialog, open: requestDialog }),
    [requestDialog, closeDialog]
  );

  return (
    <DialogsContext.Provider value={contextValue}>
      {children}
      {stack.map(({ key, open, payload, promise, Component }) => (
        <Component
          key={key}
          open={open}
          payload={payload}
          onClose={async (result) => {
            await closeDialog(promise, result);
          }}
        />
      ))}
    </DialogsContext.Provider>
  );
}

export { DialogsProvider };
